import { type ReactNode, useEffect } from 'react';
import {
    StatsigProvider,
    useClientAsyncInit,
} from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';

import type { AnalyticsUser } from './types';
import type { StatsigUser } from '@statsig/react-bindings';
import { registerFeatureLayer, type TrackLayer } from './facade';

/** Pure neutral→Statsig user mapper. Exported for unit testing. */
export function toStatsigUser(user: AnalyticsUser): StatsigUser {
    return { userID: user.id, custom: user.custom };
}

const CLIENT_KEY_FALLBACK = 'client-CT2AHP5l1X1N1q13I0v5A7asx4YCpiWIfAYssSnyXPr';
const ENV_TIER_FALLBACK = 'development';

/**
 * Resolve the Statsig client key from the build-time env, falling back to the
 * bundled key. Pure + injectable so it can be unit-tested without import.meta.
 */
export function resolveStatsigClientKey(
    env: { VITE_STATSIG_CLIENT_KEY?: string } = import.meta.env,
): string {
    const key = env.VITE_STATSIG_CLIENT_KEY?.trim();
    return key ? key : CLIENT_KEY_FALLBACK;
}

/**
 * Resolve the Statsig environment tier from the build-time env. Defaults to
 * 'development' so local `bun run dev` never reports into production Pulse.
 * CI deploy workflows set VITE_STATSIG_ENV per target (development/staging/production).
 */
export function resolveStatsigEnv(
    env: { VITE_STATSIG_ENV?: string } = import.meta.env,
): string {
    const tier = env.VITE_STATSIG_ENV?.trim();
    return tier ? tier : ENV_TIER_FALLBACK;
}

/**
 * Build the vendor-neutral event layer for a live Statsig client. Statsig only
 * receives events: the layer has no checkGate (the facade reads gates from the
 * gate store, fed by the backend's GET /api/gates), so the browser SDK
 * evaluates no gates and logs no gate exposures.
 */
function statsigTrackLayer(client: {
    // NOTE: installed @statsig/js-client@3.33.2 logEvent metadata is Record<string, string>,
    // not Record<string, unknown> as in the brief. We cast at the call-site to avoid
    // widening the FeatureLayer contract — callers on the facade side use Record<string, unknown>.
    logEvent(eventName: string, value?: string | number, metadata?: Record<string, string>): void;
}): TrackLayer {
    return {
        track: (event, value, metadata) =>
            client.logEvent(event, value, metadata as Record<string, string> | undefined),
    };
}

/**
 * Encapsulates ALL Statsig wiring: async client init (with autocapture + an
 * optional session-replay plugin), registering `track` with the facade, and the
 * provider. It is not a gate source (see statsigTrackLayer). Only
 * mounted once the user has granted the analytics category (see AnalyticsProvider),
 * so constructing/initializing the client here is consent-gated. The plugin set is
 * fixed at first construction; changing the session-replay category later reloads
 * the page (see Task 5) because Statsig caches one client per SDK key. The static
 * index.html splash covers init, so loadingComponent is null.
 */
export function AnalyticsRoot({
    user,
    sessionReplay,
    children,
}: {
    user: AnalyticsUser;
    sessionReplay: boolean;
    // Optional: AnalyticsProvider mounts this as a SIBLING of the app (not a
    // wrapper) so granting consent doesn't remount the app subtree, so there are
    // normally no children. Kept for UI that needs the Statsig React context.
    children?: ReactNode;
}) {
    const statsigUser = toStatsigUser(user);
    // Plugins are read once at mount by useClientAsyncInit. Session replay is
    // included only when its category is granted.
    const plugins = sessionReplay
        ? [new StatsigAutoCapturePlugin(), new StatsigSessionReplayPlugin()]
        : [new StatsigAutoCapturePlugin()];
    const { client } = useClientAsyncInit(
        resolveStatsigClientKey(),
        statsigUser,
        {
            environment: { tier: resolveStatsigEnv() },
            plugins,
        },
    );

    useEffect(() => {
        registerFeatureLayer(statsigTrackLayer(client));
    }, [client]);

    // Re-identify whenever any user field changes (id or locale). userKey
    // serializes the content so the effect re-runs on real changes; statsigUser
    // is intentionally not a dep (userKey captures it).
    // Fire-and-forget; idempotent under React 19 StrictMode double-invoke.
    const userKey = JSON.stringify(statsigUser);
    useEffect(() => {
        void client.updateUserAsync(statsigUser);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client, userKey]);

    return (
        <StatsigProvider client={client} loadingComponent={null}>
            {children}
        </StatsigProvider>
    );
}
